import { useCallback, useMemo, useState } from "react";

import {
  Close,
  InsertDriveFile,
  NoteAddOutlined,
  PostAddOutlined,
} from "@mui/icons-material";
import { Box, IconButton, styled, Typography } from "@mui/material";
import { DEFAULT_ACCEPTED_FILE_TYPES } from "@utils";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

export type FileDropZoneVariant = "primary" | "secondary";

export type FileDropZoneProps = {
  file: File | null;
  onFileChange: (file: File | null) => void;
  label?: string;
  accept?: string;
  variant?: FileDropZoneVariant;
};

const getVariantStyles = (
  variant: FileDropZoneVariant,
  isDragging: boolean
) => {
  if (variant === "secondary") {
    return {
      box: isDragging
        ? "border-blue-400 bg-blue-50"
        : "border-blue-300 bg-white hover:bg-blue-50",
      icon: "text-blue-400",
      text: "text-blue-400",
    };
  }
  // primary (default)
  return {
    box: isDragging
      ? "border-blue-600 bg-blue-200"
      : "border-blue-600 bg-blue-100 hover:bg-blue-200",
    icon: "text-blue-600",
    text: "text-blue-600",
  };
};

export const FileDropZone = ({
  file,
  onFileChange,
  label,
  accept = DEFAULT_ACCEPTED_FILE_TYPES,
  variant = "primary",
}: FileDropZoneProps) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const droppedFile = e.dataTransfer.files?.[0];
      if (droppedFile) {
        onFileChange(droppedFile);
      }
    },
    [onFileChange]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      onFileChange(selectedFile);
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onFileChange(null);
  };

  const isImage = file?.type.startsWith("image/");
  const previewUrl = useMemo(() => {
    if (file && isImage) {
      return URL.createObjectURL(file);
    }
    return null;
  }, [file, isImage]);

  const styles = getVariantStyles(variant, isDragging);

  if (file) {
    return (
      <Box className="flex flex-col gap-2 w-full h-full">
        <Box
          component="label"
          className={`relative flex flex-col items-center justify-center gap-2 px-4 py-8 border-2 border-dashed rounded-lg cursor-pointer transition-colors w-full min-h-[120px] flex-1 ${styles.box}`}
        >
          <IconButton
            size="small"
            onClick={handleRemove}
            className="absolute top-1 right-1"
            sx={{ padding: 0.5 }}
          >
            <Close fontSize="small" className={styles.icon} />
          </IconButton>
          {previewUrl ? (
            <img
              src={previewUrl}
              alt={file.name}
              className="max-h-16 max-w-full object-contain rounded"
            />
          ) : (
            <InsertDriveFile className={styles.icon} sx={{ fontSize: 40 }} />
          )}
          <Typography
            variant="caption"
            className={`${styles.text} text-center truncate max-w-full px-2`}
            component="span"
          >
            {file.name}
          </Typography>
          <Typography
            variant="caption"
            className={`${styles.text} opacity-70`}
            component="span"
          >
            Click to change
          </Typography>
          <VisuallyHiddenInput
            type="file"
            onChange={handleInputChange}
            accept={accept}
            max={1}
            data-testid="file-dropzone-input"
          />
        </Box>
      </Box>
    );
  }

  const Icon = variant === "primary" ? NoteAddOutlined : PostAddOutlined;

  return (
    <Box className="flex flex-col gap-2 w-full h-full">
      <Box
        component="label"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`flex flex-col items-center justify-center gap-2 px-6 py-12 border-2 border-dashed rounded-lg cursor-pointer transition-colors w-full min-h-[120px] flex-1 ${styles.box}`}
      >
        <Icon className={styles.icon} sx={{ fontSize: 32 }} />
        {label ? (
          <Typography
            variant="body2"
            className={`${styles.text} text-center font-medium min-w-[100px] w-1/2`}
            component="span"
          >
            {label}
          </Typography>
        ) : (
          <Typography
            variant="body2"
            className={`${styles.text} text-center`}
            component="span"
          >
            Drag & drop or click to select
          </Typography>
        )}
        <VisuallyHiddenInput
          type="file"
          onChange={handleInputChange}
          accept={accept}
          max={1}
          data-testid="file-dropzone-input"
        />
      </Box>
    </Box>
  );
};

export default FileDropZone;
